import { topCategorySlugs } from '@aboutyou/legacy-shared/src/localizedUrls/topCategorySlugs'
import {
  RecoSectionContent,
  TrackingPageTypeWithId,
  TrackingPromotionCreative,
} from '@aboutyou/legacy-tracking-sdk'
import PromotionLink from 'components/PromotionLink'
import { useTranslation } from 'helpers/hooks/useTranslation'
import { cdnImageUrl, getImageUrl } from 'helpers/image'
import { ElementTrackingContextProvider } from 'helpers/tracking/ElementTrackingContext'
import { RecoSliderSectionTrackingContextProvider } from 'helpers/tracking/SectionTrackingContext'
import { PromotionImpression } from 'helpers/tracking/trackerComponents/ElementTracker'
import React, { FC } from 'react'
import { Link } from 'react-router-dom'
import { useService } from 'services/utils/context'
import { usePremiumPageStore, useUserStore } from 'store/context'
import { useLocale } from 'store/features/locale/useLocale'
import styled from 'styled-components'
import HorizontalSlider from 'uiComponents/molecules/Slider/HorizontalSlider'
import ImageTile from './ImageTile'
import PremiumLandingSection from './PremiumLandingSection'
import { SectionHeaderPadded } from './PremiumSectionHeaders'

const BrandLogo = styled.div`
  min-width: 100px;
  width: 60%;
`

const useSectionTitleAndLink = () => {
  const { t } = useTranslation()
  const { locale } = useLocale()

  const gender = useUserStore(store => store.gender)
  const { female, male } = topCategorySlugs(locale)
  const genderLink = gender === 'male' ? male : female

  const today = new Date()
  const month = today.getMonth()

  if (month > 0 && month < 7) {
    return {
      sectionTitle: t('Frühjahr / Sommer Kollektionen'),
      linkTarget: `/${genderLink}/premium?seasonPdp=87309`,
    }
  }
  return {
    sectionTitle: t('Herbst / Winter Kollektionen'),
    linkTarget: `/${genderLink}/premium?seasonPdp=87308`,
  }
}

const PremiumBrandSlider: FC = () => {
  const brandIds = usePremiumPageStore(
    store => store.brandIdsForBrandImageSlider,
  )
  const { sectionTitle, linkTarget } = useSectionTitleAndLink()

  if (!brandIds || brandIds.length === 0) {
    return null
  }

  return (
    <PremiumLandingSection
      sectionTitle={
        <Link to={linkTarget}>
          <SectionHeaderPadded title={sectionTitle} />
        </Link>
      }
    >
      <RecoSliderSectionTrackingContextProvider
        headline={sectionTitle}
        recoType={RecoSectionContent.topBrands}
      >
        <HorizontalSlider
          slideCount={3}
          slideCountMedium={4}
          slideCountLarge={5}
          disableHeaderControl
        >
          {brandIds.map(id => (
            <BrandImageTile key={id} brandId={id} />
          ))}
        </HorizontalSlider>
      </RecoSliderSectionTrackingContextProvider>
    </PremiumLandingSection>
  )
}

export default PremiumBrandSlider

const BrandImageTile: FC<{ brandId: string }> = ({ brandId }) => {
  const service = useService()
  const brand = service.brands.getByBrandId(brandId)

  if (!brand || !brand.rectangleImageHash) {
    return null
  }

  const imageUrl = getImageUrl(brand.rectangleImageHash, {
    trim: false,
    width: 600,
    height: 800,
    bg: 'f2f2f2',
  })

  const logoUrl =
    brand.transparentImageHash &&
    cdnImageUrl(brand.transparentImageHash, {
      transparent: true,
      width: 400,
      height: 200,
    })
  if (!imageUrl) {
    return null
  }

  return (
    <ElementTrackingContextProvider>
      <PromotionImpression<HTMLDivElement>
        type={TrackingPageTypeWithId.Brand}
        id={brand.id}
        name={brand.name}
        url={brand.url}
        source={TrackingPromotionCreative.BrandReco}
        useIdForDestination
      >
        {({ ref, onClickCapture }) => (
          <PromotionLink onClickCapture={onClickCapture} to={brand.url}>
            <BrandTile ref={ref} data-testid="BrandTile">
              <ImageTile imageUrl={imageUrl}>
                <BrandLogo>
                  <img src={logoUrl} alt={brand.name} />
                </BrandLogo>
              </ImageTile>
            </BrandTile>
          </PromotionLink>
        )}
      </PromotionImpression>
    </ElementTrackingContextProvider>
  )
}

const BrandTile = styled.div`
  height: 100%;
`
